import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import IShop from "../../entities/IShop";
import IUser from "../../entities/IUser";
import ShopService from "../../services/ShopService";
import UserService from "../../services/UserService";
import { getErrorMessage, unwrapData, unwrapList } from "../../services/apiUtils";
import { SHOP_STATUS_ACTIVE, SHOP_STATUS_OPTIONS } from "./shopUtils";

const customerLabel = (u: IUser) => {
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.username || u.emailId || `Customer #${u.userId}`;
};

type ShopFormDialogProps = {
  open: boolean;
  shopId?: number | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
};

const emptyShop: IShop = {
  shopId: 0,
  shopName: "",
  shopNumber: "",
  customerId: undefined,
  customerName: "",
  profileImage: "",
  shopStatus: SHOP_STATUS_ACTIVE,
  followers: 0,
  sold: 0,
  items: 0,
  rating: 0,
  joined: 0,
  chatPerformance: 0,
};

const shopSchema = yup.object().shape({
  shopName: yup.string().trim().required("Shop name is required"),
  customerId: yup
    .number()
    .typeError("Customer ID must be a number")
    .required("Customer ID is required"),
});

const ShopFormDialog: React.FC<ShopFormDialogProps> = ({
  open,
  shopId,
  onClose,
  onSaved,
  onError,
  onSuccess,
}) => {
  const isEditMode = !!shopId;
  const [initialValues, setInitialValues] = useState<IShop>(emptyShop);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<IUser[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingCustomers(true);
    UserService.getUsers()
      .then((res: any) => setCustomers(unwrapList<IUser>(res)))
      .catch(() => setCustomers([]))
      .finally(() => setLoadingCustomers(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!isEditMode) {
      setInitialValues(emptyShop);
      return;
    }
    setLoading(true);
    ShopService.getShopById(shopId as number)
      .then((res: any) => {
        const data = unwrapData(res) as IShop;
        setInitialValues({
          shopId: data?.shopId ?? shopId,
          shopName: data?.shopName ?? "",
          shopNumber: data?.shopNumber ?? "",
          customerId: data?.customerId ?? undefined,
          customerName: data?.customerName ?? "",
          profileImage: data?.profileImage ?? "",
          shopStatus: data?.shopStatus ?? SHOP_STATUS_ACTIVE,
          followers: data?.followers ?? 0,
          sold: data?.sold ?? 0,
          items: data?.items ?? 0,
          rating: data?.rating ?? 0,
          joined: data?.joined ?? 0,
          chatPerformance: data?.chatPerformance ?? 0,
        });
      })
      .catch((err: any) => {
        onError(getErrorMessage(err, "Could not load this shop."));
      })
      .finally(() => setLoading(false));

  }, [open, isEditMode, shopId]);

  const handleSubmit = (values: IShop, { setSubmitting }: any) => {

    const matchedCustomer = customers.find((c) => c.userId === Number(values.customerId));
    const payload: IShop = {
      ...values,
      customerName: matchedCustomer ? customerLabel(matchedCustomer) : values.customerName,
    };

    const request = isEditMode
      ? ShopService.updateShop(shopId as number, payload)
      : ShopService.createShop(payload);

    request
      .then(() => {
        onSuccess(isEditMode ? "Shop updated successfully." : "Shop created successfully.");
        onSaved();
        onClose();
      })
      .catch((err: any) => {
        onError(
          getErrorMessage(
            err,
            isEditMode ? "Could not update the shop. Please try again." : "Could not create the shop. Please try again."
          )
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle>{isEditMode ? "Edit Shop" : "Create Shop"}</DialogTitle>
      {loading ? (
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </DialogContent>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={shopSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Box display="grid" gap="16px" gridTemplateColumns="repeat(2, minmax(0,1fr))">
                  <TextField
                    fullWidth
                    label="Shop Name"
                    name="shopName"
                    value={values.shopName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!touched.shopName && !!errors.shopName}
                    helperText={touched.shopName && (errors.shopName as string)}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    label="Shop Number"
                    name="shopNumber"
                    value={values.shopNumber || ""}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="shopStatus"
                    value={values.shopStatus ?? SHOP_STATUS_ACTIVE}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  >
                    {SHOP_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    select
                    label="Customer"
                    name="customerId"
                    value={values.customerId ?? ""}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!touched.customerId && !!errors.customerId}
                    helperText={
                      (touched.customerId && (errors.customerId as string)) ||
                      (loadingCustomers ? "Loading customers…" : undefined)
                    }
                    disabled={loadingCustomers}
                    sx={{ gridColumn: "span 2" }}
                  >
                    {customers.map((c) => (
                      <MenuItem key={c.userId} value={c.userId}>
                        {customerLabel(c)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Profile Image URL"
                    name="profileImage"
                    value={values.profileImage || ""}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export default ShopFormDialog;
